import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../App";
import { Button } from "./ui/button";
import { Menu, X, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Header = ({ onBookingClick }) => {
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
  ];

  const isHome = location.pathname === "/";

  const scrollToSection = (id) => {
    if (isHome) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/#${id}`);
    }
    setMobileMenuOpen(false);
  };

  const goTo = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/wxjoscue_Logo_DKP.jpeg" 
              alt="DKP Logo" 
              className="h-12 w-12 rounded-full object-cover"
            />
            <h1 className="text-2xl font-bold text-gray-900">
              DAKHLA KITE PLANET
            </h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection("home")}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => goTo("/lessons")}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              data-testid="nav-lessons"
            >
              {t.nav.lessons}
            </button>
            <button
              onClick={() => goTo("/packages")}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              data-testid="nav-packages"
            >
              {t.nav.packages}
            </button>
            <button
              onClick={() => goTo("/blog")}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              data-testid="nav-blog"
            >
              Blog
            </button>
            <button
              onClick={() => scrollToSection("about")}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              {t.nav.contact}
            </button>

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <Globe className="h-4 w-4" />
                  {languages.find((l) => l.code === language)?.flag}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? "bg-blue-50" : ""}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              onClick={onBookingClick}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-6"
            >
              {t.nav.bookNow}
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-blue-600"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection("home")}
                className="text-gray-700 hover:text-blue-600 transition-colors text-left font-medium"
              >
                {t.nav.home}
              </button>
              <button
                onClick={() => goTo("/lessons")}
                className="text-gray-700 hover:text-blue-600 transition-colors text-left font-medium"
              >
                {t.nav.lessons}
              </button>
              <button
                onClick={() => goTo("/packages")}
                className="text-gray-700 hover:text-blue-600 transition-colors text-left font-medium"
              >
                {t.nav.packages}
              </button>
              <button
                onClick={() => goTo("/blog")}
                className="text-gray-700 hover:text-blue-600 transition-colors text-left font-medium"
              >
                Blog
              </button>
              <button
                onClick={() => scrollToSection("about")}
                className="text-gray-700 hover:text-blue-600 transition-colors text-left font-medium"
              >
                {t.nav.about}
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-gray-700 hover:text-blue-600 transition-colors text-left font-medium"
              >
                {t.nav.contact}
              </button>
              <Button
                onClick={onBookingClick}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white w-full"
              >
                {t.nav.bookNow}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
